import JwtTokenObject from "@/interfaces/jwttokenobject";
import jwt from "jsonwebtoken";
import EventEmitter from "eventemitter3";

export const JwtTokenEvents = {
  REFRESH_TOKEN_FAILED: Symbol()
};

export class JwtToken {
  isRefreshingSuspended = false;

  get currentTokenString(): string | undefined {
    return this.impersonatedJwtTokenString || this.jwtTokenString;
  }
  get currentTokenObject(): JwtTokenObject | undefined {
    return this.impersonatedJwtTokenObject || this.jwtTokenObject;
  }
  public jwtTokenString?: string;
  public jwtTokenObject?: JwtTokenObject;

  public impersonatedJwtTokenString?: string;
  public impersonatedJwtTokenObject?: JwtTokenObject;

  public eventEmitter = new EventEmitter();

  static singletonInstance?: JwtToken;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static singleton(): JwtToken {
    return this.singletonInstance || (this.singletonInstance = new this());
  }

  async tryLoadJwtToken(): Promise<void> {
    if (this.isRefreshingSuspended) {
      return;
    }

    if (localStorage.refreshToken) {
      try {
        if (
          !(
            this.jwtTokenString &&
            this.jwtTokenObject &&
            this.jwtTokenObject.exp - 60 > Date.now() / 1000
          )
        ) {
          await this.refreshToken(localStorage.refreshToken);
        }
        if (
          localStorage.impersonatedRefreshToken &&
          localStorage.impersonatedRefreshToken !== "undefined"
        ) {
          if (
            !(
              this.impersonatedJwtTokenString &&
              this.impersonatedJwtTokenObject &&
              this.impersonatedJwtTokenObject.exp - 60 > Date.now() / 1000
            )
          ) {
            await this.refreshImpersonatedToken(
              localStorage.impersonatedRefreshToken
            );
          }
        }
      } catch (e) {
        this.isRefreshingSuspended = true;
        this.eventEmitter.emit(JwtTokenEvents.REFRESH_TOKEN_FAILED, e);
      }
    }
  }

  async refreshToken(refreshToken: string) {
    const response = await fetch("/api/authentication_token/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        ["refresh_token"]: refreshToken
      }),
      credentials: "same-origin"
    });
    if (!response.ok) {
      this.clearJwtToken();
      localStorage.refreshToken = undefined;
      throw "一定時間経過したためログアウトしました";
    }
    const ret = await response.json();
    this.setJwtToken(ret.token);
    await this.saveRefreshToken(ret.refresh_token);
  }

  async refreshImpersonatedToken(refreshToken: string) {
    const response = await fetch("/api/authentication_token/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        ["refresh_token"]: refreshToken
      }),
      credentials: "same-origin"
    });
    if (!response.ok) {
      this.clearImpersonatedJwtToken();
      localStorage.impersonatedRefreshToken = undefined;
      throw "一定時間経過したためログアウトしました";
    }
    const ret = await response.json();
    this.setImpersonatedJwtToken(ret.token);
    await this.saveImpersonatedRefreshToken(ret.refresh_token);
  }

  setJwtToken(token: string): void {
    this.jwtTokenString = token;
    this.jwtTokenObject = jwt.decode(token) as JwtTokenObject;
  }

  async saveRefreshToken(refreshToken?: string) {
    localStorage.refreshToken = refreshToken;
  }

  setImpersonatedJwtToken(token: string): void {
    this.impersonatedJwtTokenString = token;
    this.impersonatedJwtTokenObject = jwt.decode(token) as JwtTokenObject;
  }

  async saveImpersonatedRefreshToken(refreshToken?: string) {
    localStorage.impersonatedRefreshToken = refreshToken;
  }

  clearJwtToken(): void {
    this.jwtTokenString = undefined;
    this.jwtTokenObject = undefined;
  }

  clearImpersonatedJwtToken(): void {
    this.impersonatedJwtTokenString = undefined;
    this.impersonatedJwtTokenObject = undefined;
  }

  isGranted(roles: string[]) {
    const jwtTokenObject = this.currentTokenObject;
    if (!jwtTokenObject) {
      return false;
    }
    const jwtRoles = (jwtTokenObject.roles || []).slice(0);
    if (this.impersonatedJwtTokenObject) {
      jwtRoles.push("ROLE_IMPERSONATOR");
    }
    if (jwtTokenObject.advertiser?.isDeleted) {
      return false;
    }
    for (const r of roles) {
      if (jwtRoles.indexOf(r) < 0) {
        return false;
      }
    }

    return true;
  }

  isImpersonatorGranted(roles: string[]) {
    const jwtTokenObject = this.jwtTokenObject;
    if (!jwtTokenObject) {
      return false;
    }
    const jwtRoles = (jwtTokenObject.roles || []).slice(0);
    for (const r of roles) {
      if (jwtRoles.indexOf(r) < 0) {
        return false;
      }
    }

    return true;
  }
}

export default JwtToken;
